import React, { useState, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/* ── chrome icons (no libs) ─────────────────────────────────────────── */
const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
);
const ExpandIcon = ({ expanded }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {expanded
            ? <><polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" /></>
            : <><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></>}
    </svg>
);

/**
 * SbrDrawer — the reusable right-side slide-over panel for the "sbr" design system.
 *
 * Renders a react-bootstrap <Offcanvas> with the standard sbr chrome:
 *   • header  — icon badge + title + subtitle + (optional) expand toggle + close
 *   • body    — scrollable; put your <Form> / content here (children)
 *   • footer  — optional sticky footer; put buttons here (submit a form inside the
 *               body with the HTML `form="<id>"` attribute so the footer can stay
 *               outside the scroll area).
 *
 * Props:
 *   show, onHide   — visibility control (required)
 *   onShow         — called once each time the drawer opens (e.g. to reset a form;
 *                    replaces react-bootstrap Modal's onShow)
 *   title          — header title (string or node)
 *   subtitle       — header subtitle (optional)
 *   icon           — JSX icon node shown in the header badge (optional)
 *   children       — scrollable body content
 *   footer         — JSX for the sticky footer (optional)
 *   expandable     — show the expand/collapse width toggle (default true; ignored when secondary)
 *   secondary      — narrower 520px panel for a stacked/secondary drawer (default false)
 *   backdrop       — Offcanvas backdrop: true | false | "static" (default "static")
 *   width          — optional inline width override (e.g. "760px")
 *   className      — extra class(es) for the Offcanvas
 */
const SbrDrawer = ({
    show,
    onHide,
    onShow,
    title,
    subtitle,
    icon,
    children,
    footer,
    expandable = true,
    secondary = false,
    backdrop = 'static',
    width,
    className = '',
    // react-bootstrap's Offcanvas traps focus inside itself (enforceFocus
    // defaults to true). Any panel rendered as a SIBLING of the drawer — the
    // customer message prompt, the invoice share panel — is outside that DOM,
    // so clicking into its fields put the caret there and the trap immediately
    // pulled focus back: the field looked dead and nothing could be typed.
    // Callers release the trap while such a panel is open.
    enforceFocus = true,
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    // Offcanvas has no Modal-style `onShow`; fire it whenever the drawer opens.
    useEffect(() => { if (show && typeof onShow === 'function') onShow(); }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const cls = [
        'sbr-drawer',
        secondary ? 'sbr-drawer-2' : '',
        expanded ? 'expanded' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            backdrop={backdrop}
            backdropClassName={secondary ? 'level-2' : undefined}
            enforceFocus={enforceFocus}
            className={cls}
            style={width ? { width } : undefined}
        >
            <div className="sbr drawer-shell">
                <div className="sbm-head">
                    {icon && <div className="ico">{icon}</div>}
                    <div className="htx">
                        <div className="ttl">{title}</div>
                        {subtitle && <div className="sub">{subtitle}</div>}
                    </div>
                    <div className="hactions">
                        {expandable && !secondary &&
                            <button type="button" className="x" onClick={() => setExpanded(e => !e)}
                                title={expanded ? t('sbrDrawer.collapsePanel', 'Collapse panel') : t('sbrDrawer.expandPanel', 'Expand panel')}
                                aria-label={expanded ? t('sbrDrawer.collapsePanel', 'Collapse panel') : t('sbrDrawer.expandPanel', 'Expand panel')}>
                                <ExpandIcon expanded={expanded} />
                            </button>
                        }
                        <button type="button" className="x" onClick={onHide} aria-label={t('common.close', 'Close')}><XIcon /></button>
                    </div>
                </div>

                <div className="sbm-body drawer-scroll">{children}</div>

                {footer && <div className="sbm-foot drawer-foot">{footer}</div>}
            </div>
        </Offcanvas>
    );
};

export default SbrDrawer;
